import { useIsPreviewOrDemo } from "../../../contexts/IsPreviewOrDemoContext";
import {
  Configuration,
  StripeRegistryDetails,
} from "../../../resources/modelTypes";
import { themeTypographyResetStyles } from "../../../website/components/themeTypographyResetStyles";
import { selectors } from "../../cypressSelectors";
import { SectionComponentProps } from "../../types";
import { DisplayPhoto } from "../DisplayPhoto";
import { useWebsiteWidth } from "../WebsiteWidthContext";
import CashRegistryDefaultImage from "../assets/cashRegistryDefaultSmall.webp";
import { StripeContributionModal } from "./StripeContributionModal";
import { css } from "@emotion/react";
import { styleUtils, theme } from "@minted/minted-components";
import React, { memo, useCallback, useState } from "react";

const styles = {
  card: (isMobile: boolean) => css`
    background-color: ${theme.colors.gray100};
    border: ${styleUtils.rem(1)} solid ${theme.colors.border};
    border-radius: ${styleUtils.rem(8)};
    color: ${theme.colors.textPrimary};
    ${isMobile && `flex: 0 0 calc(50% - ${theme.spacing.x2});`}
    min-height: ${isMobile ? styleUtils.rem(208) : styleUtils.rem(332)};
    width: ${isMobile ? "100%" : styleUtils.rem(256)};
  `,
  imageInner: css`
    height: 100%;
    width: 100%;

    & img {
      height: 100%;
      object-fit: cover;
      width: 100%;
    }
  `,
  imageWrapper: css`
    aspect-ratio: 1;
    border-top-left-radius: ${styleUtils.rem(8)};
    border-top-right-radius: ${styleUtils.rem(8)};
    cursor: pointer;
    overflow: hidden;
    width: 100%;
  `,
  subtitle: css`
    ${theme.typeStyles.bodyExtraSmallStyles};
    color: ${theme.colors.textSecondary};
    display: flex;
  `,
  textBlock: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.base};
    padding-bottom: ${theme.spacing.x4};
    padding-left: ${theme.spacing.x4};
    padding-right: ${theme.spacing.x4};
    padding-top: ${theme.spacing.x4};
    text-align: left;
    ${theme.media.lessThan(theme.breakpoints.small)(`
      padding-bottom: ${theme.spacing.x3};
      padding-left: ${theme.spacing.x2};
      padding-right: ${theme.spacing.x2};
      padding-top: ${theme.spacing.x2};
    `)};
  `,
  title: css`
    display: flex;
    overflow-wrap: break-word;
    word-break: break-word;
    ${theme.typeStyles.titleSmallStyles};
  `,
};

type DisplayStripeRegistrySectionProps = {
  configuration: Configuration;
} & SectionComponentProps;

const DisplayStripeRegistrySection = ({
  configuration,
  section,
}: DisplayStripeRegistrySectionProps) => {
  const { isDemo } = useIsPreviewOrDemo();
  const details = section.sectionDetails as StripeRegistryDetails;
  const width = useWebsiteWidth();
  const isMobile: boolean = width !== null && width < 600;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = useCallback(() => {
    if (isDemo) {
      return;
    }
    setIsModalOpen(true);
  }, [isDemo]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return (
    <>
      <div
        css={[styles.card(isMobile), themeTypographyResetStyles]}
        data-cy={selectors.stripeRegistry_websiteCard}
      >
        <div
          css={styles.imageWrapper}
          data-cy={selectors.stripeRegistry_websiteCard_image}
          onClick={openModal}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              openModal();
            }
          }}
          role="button"
          tabIndex={0}
        >
          <div css={styles.imageInner}>
            <DisplayPhoto
              configuration={configuration}
              defaultImage={CashRegistryDefaultImage}
              isRegistryPhoto
              section={section}
            />
          </div>
        </div>

        <div
          css={styles.textBlock}
          data-cy={selectors.stripeRegistry_websiteCard_text}
          onClick={openModal}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              openModal();
            }
          }}
          role="button"
          tabIndex={0}
        >
          <div css={styles.subtitle}>Cash fund</div>
          {details.title && (
            <div
              css={styles.title}
              data-cy={selectors.stripeRegistry_websiteCard_title}
            >
              {details.title}
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <StripeContributionModal
          configuration={configuration}
          onClose={closeModal}
          section={section}
        />
      )}
    </>
  );
};

const MemoizedDisplayStripeRegistrySection = memo(DisplayStripeRegistrySection);

export { MemoizedDisplayStripeRegistrySection as DisplayStripeRegistrySection };
